import { WinCounter } from '@/managers/winning/WinCounter'
import { SymbolType } from '@/containers/symbols/SymbolType'
import { SYMBOL_KEY_TO_ID, SYMBOL_KEY_TO_MULTIPLIER } from '@/gameConfigs/symbolMap'
import { IModel } from '@/model/IModel'
import { IRoundModel } from '@/model/IRoundModel'
import { ISpinModel } from '@/model/ISpinModel'
import { CombinationsData, IInitResponse, ISpinResponse, RealityCheckData, SymbolData } from '@/modules'
import GameSignals from '@/signals/GameSignals'
import { GameStateMachine } from '@/states/GameStateMachine'
import { GameStatesEnum } from '@/states/GameStatesEnum'

export class Model implements IModel {
    public roundState: IRoundModel
    public spinState: ISpinModel
    public winCounter: WinCounter
    public isBonusGameNow: boolean
    public realityCheckData: RealityCheckData
    public bets: number[]
    public isPremiumSpinsActive: boolean
    public winCurrency: number
    public balance: number
    public isBuyBonusPending: boolean
    public buyBonusPickPosition: number
    public isFreeSpinsRestore: boolean
    public combinationsData: CombinationsData
    public winPositions: number[][]
    public initialSlotGrid: SymbolData[][]
    public cascadeIndex: number
    public bigWinLevel: number | null
    public bonusBuyBet: number | null
    public tournamentData: any | null

    private _stateMachine: GameStateMachine
    private _currentBet: number
    private _initData: IInitResponse
    private _spinData: ISpinResponse

    public constructor() {
        this.isBonusGameNow = false
        this.winCounter = new WinCounter()
        this.balance = 0
        this.tournamentData = null
        this.bets = []
        this.isBuyBonusPending = false
        this.buyBonusPickPosition = 0
    }

    public init() {
        this._stateMachine = new GameStateMachine(this)
        this._stateMachine.setState(GameStatesEnum.BEFORE_LOADING_STATE)

        this.resetRoundState()
        this.resetSpinState()
    }

    public get initData(): IInitResponse {
        return this._initData
    }

    public set initData(value: IInitResponse) {
        this._initData = value

        const coinValues = value.game.coin_values
            ? String(value.game.coin_values)
                  .split(';')
                  .map((v: string) => Number(v))
                  .filter((v: number) => Number.isFinite(v) && v > 0)
            : []

        const safeCoinValueList = coinValues.length
            ? coinValues
            : [
                  0.01, 0.02, 0.03, 0.04, 0.05, 0.06, 0.07, 0.08, 0.09, 0.1, 0.12, 0.16, 0.2, 0.25, 0.3, 0.4, 0.5, 0.6,
                  0.7, 0.75, 0.8, 0.9, 1, 1.2, 1.4, 1.5, 2, 2.5, 3, 5, 10, 15, 20,
              ]

        this.balance = Number(value.player.balance ?? 0)
        this.bets = safeCoinValueList.map((bet) => bet * this.fixedCoinsBet)
        this.initialSlotGrid = this.parseMatrixToSlotGrid(value.previous_round?.matrix || '')
        this.resetCurrentBet()

        const previousCoinValue = Number(value.previous_round?.coin_value)
        if (Number.isFinite(previousCoinValue) && previousCoinValue > 0) {
            this.currentBet = previousCoinValue * this.fixedCoinsBet
        }

        const nextRound = Array.isArray(value.next_round) ? null : (value.next_round as any)
        const nextRoundType = String(nextRound?.type || '').toLowerCase()
        const isFsRestore =
            nextRoundType === 'freespins' ||
            nextRoundType === 'freespin'

        if (isFsRestore && nextRound) {
            const previousRoundWin = Number(value.previous_round?.win_amount ?? 0)
            const totalSpins = Number(nextRound.num_spins)
            const spinsLeft = Number(nextRound.spins_left)
            const totalFsWin = Number(nextRound.total_fs_win_amount ?? 0)

            this.spinData = {
                balance: this.balance,
                base_win_currency: Number.isFinite(previousRoundWin) ? previousRoundWin : 0,
                bonus: {
                    bonus_amount: 0,
                    bonus_win_currency: 0,
                    freebet_count: 0,
                    freebet_given: 0,
                    freebets_position: [[]],
                    jackpot_won: [],
                    multipliers: [],
                    scatter_count: 0,
                    scatters_position: [],
                },
                currency: value.player.currency,
                freebet: {
                    amount_left: Number.isFinite(spinsLeft) ? Math.max(0, spinsLeft) : 0,
                    coin_amount: this.fixedCoinsBet,
                    coin_value: Number(nextRound.coin_value ?? value.previous_round.coin_value ?? this.currentCoinBet),
                    logic: { level: 0, lines_played: 0 },
                    total_amount_given: Number.isFinite(totalSpins) ? Math.max(0, totalSpins) : 0,
                    total_coin_win: Number.isFinite(totalFsWin) ? totalFsWin : 0,
                    total_win: Number.isFinite(totalFsWin) ? totalFsWin : 0,
                    triggered_win_amount: 0,
                    triggered_win_amount_currency: 0,
                    triggered_win_bonus_amount: 0,
                    triggered_win_bonus_amount_currency: 0,
                },
                freeround: null,
                session_token: '',
                slot_grid: this.initialSlotGrid.length ? [this.initialSlotGrid] : [],
                status: 0,
                win_coin: Number.isFinite(previousRoundWin) ? previousRoundWin : 0,
                win_currency: Number.isFinite(previousRoundWin) ? previousRoundWin : 0,
                winning_combinations: [[]],
            }
        }

        this.realityCheckData = {
            startSessionTime: Date.now(),
            totalSessionBet: 0,
            totalSessionWin: 0,
        }
    }

    public get spinData(): ISpinResponse {
        return this._spinData
    }

    public set spinData(value: ISpinResponse) {
        this._spinData = value
        if (value.balance > 0) {
            this.balance = value.balance
        }
        this.winCurrency = value.win_currency
    }

    public get currentBet(): number {
        return this._currentBet
    }

    public set currentBet(value: number) {
        if (!this.maxBet || value <= this.maxBet) {
            this._currentBet = value
            GameSignals.betHasChanged.emit(this._currentBet, this.currentBaseBet)
        }
    }

    public resetCurrentBet() {
        this.currentBet = this.defaultCoinValue * this.fixedCoinsBet
    }

    get currentBaseBet(): number {
        return this._currentBet
    }

    public get currentCoinBet(): number {
        return +(this._currentBet / this.fixedCoinsBet).toFixed(2)
    }

    public get currentSpinBet(): number {
        return this.currentBet
    }

    get fixedCoinsBet(): number {
        return Number(this._initData?.game?.num_coins || this._initData?.game?.fixed_coins || 20)
    }

    get defaultCoinValue(): number {
        return Number(this._initData?.game?.default_coin_value || 1)
    }

    get maxBet(): number {
        return Number(this._initData?.game?.gamble_max_bet || 0)
    }

    get numColumns(): number {
        return Number(this._initData?.game?.num_columns || 6)
    }

    get numPaylines(): number {
        return Number(this._initData?.game?.num_paylines || 1)
    }

    public freeRoundsActive(): boolean {
        return !!this.spinData?.freeround?.is_active
    }

    public isPromoSpinsRound(): boolean {
        const spinPromoType = String(this._spinData?.promo_details?.type || '').toLowerCase()
        const initPromoType = String(this._initData?.next_round?.type || '').toLowerCase()

        return (
            spinPromoType === 'promospins' ||
            spinPromoType === 'promospin' ||
            initPromoType === 'promospins' ||
            initPromoType === 'promospin'
        )
    }

    public resetRoundState() {
        this.roundState = {
            isRestoredRound: false,
            isFreeSpins: false,
        }
    }

    public resetSpinState() {
        this.spinState = {
            spinStartTime: Date.now(),
            bonusGridIndex: 0,
            bonusGridsCount: 0,
            isHoldAndWinActive: false,
            needsBetCooldown: true,
            isIdleStateWinSkipped: false,
            isWinSkipped: false,
        }

        this.cascadeIndex = 0
        this.bigWinLevel = null
    }

    private parseMatrixToSlotGrid(matrix: string): SymbolData[][] {
        const rows = matrix
            .split(';')
            .map((row) => row.trim())
            .filter((row) => row.length > 0)

        if (!rows.length) return []

        const rowCount = rows.length
        const colCount = rows[0].length
        const reels: SymbolData[][] = []

        for (let col = 0; col < colCount; col++) {
            reels[col] = []
            for (let row = 0; row < rowCount; row++) {
                const symbolKey = String(rows[row][col] || '').toLowerCase()
                const multiplier = SYMBOL_KEY_TO_MULTIPLIER[symbolKey] ?? null
                reels[col].push({
                    id: SYMBOL_KEY_TO_ID[symbolKey] ?? SymbolType.LP_1,
                    ...(multiplier ? { multiplier } : {}),
                })
            }
        }

        return reels
    }
}
